// RNWY 2.0 · P1 — the CFG rate card shape, mirroring the backend basix.da/Models/RnwyCfg.cs
// (camelCase JSON on the wire). Stored as one settings blob (Rnwy.CfgJson); the pricing engine
// (P3) reads it. This is the COMPANY rate card, tuned in Settings → RNWY, never per-proposal.

// dart (2026-07-16, Chad) — `categoryKey` links the item to a category by its STABLE key (assigned by
// dragging under a header). Absent → the scope builder's key→group fallback. `category` is a LEGACY
// name-based link kept only so pre-key data still resolves. Mirrors basix.da.
export interface RnwyCatalogItem {
  key: string; label: string; base: number; categoryKey?: string; category?: string;
}

// dart (2026-07-16, Chad) — categories are editable DATA: a stable `key` (never changes) + an editable
// display `name`. Renaming touches only the name, so items (linked by key) are never disturbed. Absent
// on an old CFG → the scope builder seeds DEFAULT_CATEGORIES. Mirrors basix.da RnwyCategory.
export interface RnwyCategory { key: string; name: string; brands?: string[]; }
// item 681(E) (Chad) — "Equipment we control with RNWY" as a categorized list (category → products),
// e.g. { name: 'Video distribution', items: ['AV Pro Edge', ...] }. Maintained in Settings; each venue
// on a quote checks the products it has, which auto-write into that venue's scope. Mirrors basix.da.
export interface RnwyEquipmentCategory { name: string; items: string[]; }
export interface RnwySystemSize { key: string; label: string; base: number; perpetualPct: number; }
export interface RnwyHelpDeskTierPrices { gold: number; platinum: number; titanium: number; }
export interface RnwyHelpDeskCfg { firstVenue: RnwyHelpDeskTierPrices; addOnVenue: RnwyHelpDeskTierPrices; }
// RNWY 2.0 · P5 — SwitchPilot unit prices (mirrors basix.da RnwySwitchPilotCfg). Config units are
// one-time; license units are ANNUAL per-switch tiers; perpetual buy-out = yearly × years × factor.
export interface RnwySwitchPilotCfg {
  initConfigUnit: number;
  perSwitchConfigUnit: number;
  baseLicenseUnit: number;
  tier1LicenseUnit: number;
  tier2LicenseUnit: number;
  tier3LicenseUnit: number;
  tier4LicenseUnit: number;
  // item 647 — SwitchPilot-scoped markups (config lines / subscription lines)
  configIncrease: number;
  subscriptionIncrease: number;
  monthlyFloor: number; // item 647 — SwitchPilot monthly minimum ($/mo)
}

export interface RnwyCfg {
  configIncrease: number;
  perpetualIncrease: number;
  subscriptionIncrease: number;
  // item 647 — help-desk multiplier split by scope (first venue / add-on venue)
  helpDeskFirstIncrease: number;
  helpDeskAddOnIncrease: number;
  twoYearConfigDiscount: number;
  fiveYearConfigDiscount: number;
  // item 647 — perpetual buy-in = subscription monthly × this many months (default 6), same for
  // both sub options; a one-time amount. (Replaced the old 2yr/5yr buy-in fractions.)
  buyInMonths: number;
  // item 662 — integrator's suggested-resale markup (multiplier); >1 prints a "suggested resale"
  // figure on the PDF. 0/1 = off. Global default in Settings; per-proposal override on Step 3.
  suggestedResaleMarkup: number;
  monthlyLicenseFloor: number;
  systemSizes: RnwySystemSize[];
  helpDesk: RnwyHelpDeskCfg;
  catalog: RnwyCatalogItem[];
  // dart (2026-07-16, Chad) — editable categories (rename). Absent → the scope builder's
  // DEFAULT_CATEGORIES (the 9 seeded groups). Mirrors basix.da RnwyCfg.Categories.
  categories?: RnwyCategory[];
  // dart (2026-07-16, Chad) — ONE shared brand pool (Chad: "I don't mind all the brands being
  // shared"). Managed in Settings; every category's brand dropdown on a quote draws from it.
  brands?: string[]; // RETIRED (item 681) — kept only so old data seeds `sources`; no longer edited/used.
  // item 681 (Chad) — the "Sources" pool (individual controllable sources included at a venue), the
  // repurposed old Brands list. Each venue multi-selects from it; picks auto-write into scope.
  sources?: string[];
  // item 681(E) (Chad) — categorized "equipment we control" list; each venue picks products from it.
  controlledEquipment?: RnwyEquipmentCategory[];
  switchPilotCfg: RnwySwitchPilotCfg;
  // dart (Chad, 2026-07-15) — editable module NAMES for the SOW "System scope" line, keyed by the
  // stable module id (concourse/aviator/switchPilot/thirdParty). Absent/blank → the scope builder's
  // DEFAULT_MODULE_LABELS. Mirrors basix.da RnwyCfg.ModuleLabels.
  moduleLabels?: Record<string, string>;
}

export const emptySwitchPilotCfg = (): RnwySwitchPilotCfg => ({
  initConfigUnit: 250,
  perSwitchConfigUnit: 125, // dart 2026-07-16 — match Chad's Schedule B ($125/switch, was 99)
  baseLicenseUnit: 1185,
  tier1LicenseUnit: 85,
  tier2LicenseUnit: 75,
  tier3LicenseUnit: 60,
  tier4LicenseUnit: 55,
  configIncrease: 1,
  subscriptionIncrease: 1,
  monthlyFloor: 99.99,
});

// tolerant parse — the backend always sends a fully-seeded object, but guard anyway
export const parseCfg = (json: string | null | undefined): RnwyCfg | null => {
  if (!json || !json.trim()) return null;
  try {
    const raw = JSON.parse(json) as RnwyCfg & { helpDeskIncrease?: number };
    // item 647 — a FROZEN proposal/global snapshot saved BEFORE the help-desk multiplier split lacks
    // the two new fields. Default them so they never read as 0 (which would zero the help-desk price):
    // fall back to the old single `helpDeskIncrease` if present, else 1 (a no-op multiplier).
    return {
      ...raw,
      helpDeskFirstIncrease: raw.helpDeskFirstIncrease ?? raw.helpDeskIncrease ?? 1,
      helpDeskAddOnIncrease: raw.helpDeskAddOnIncrease ?? raw.helpDeskIncrease ?? 1,
      buyInMonths: raw.buyInMonths ?? 6, // item 647 — default on snapshots that lack it
      suggestedResaleMarkup: raw.suggestedResaleMarkup ?? 0, // item 662 — default off on old snapshots
      moduleLabels: raw.moduleLabels ?? undefined, // dart 2026-07-15 — scope builder falls back to defaults
      // item 647 — default the new SwitchPilot multipliers to 1 on a pre-item-9 snapshot
      switchPilotCfg: raw.switchPilotCfg ? {
        ...raw.switchPilotCfg,
        configIncrease: raw.switchPilotCfg.configIncrease ?? 1,
        subscriptionIncrease: raw.switchPilotCfg.subscriptionIncrease ?? 1,
        monthlyFloor: raw.switchPilotCfg.monthlyFloor ?? 99.99,
      } : raw.switchPilotCfg,
    };
  } catch {
    return null;
  }
};

export const money = (v: number): string => (v ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// dart (2026-07-16, Chad) — the shared brand pool. Prefer cfg.brands; if it's empty (a CFG saved
// under the earlier per-category design), fall back to the union of any per-category brands so
// nothing already typed is lost. De-duped, order-preserving.
export const brandPool = (cfg: RnwyCfg | null | undefined): string[] => {
  if (!cfg) return [];
  if (cfg.brands && cfg.brands.length) return cfg.brands;
  const seen = new Set<string>();
  const out: string[] = [];
  (cfg.categories ?? []).forEach((c) => (c.brands ?? []).forEach((b) => {
    const t = (b ?? '').trim();
    if (t && !seen.has(t)) { seen.add(t); out.push(t); }
  }));
  return out;
};

// item 681 (Chad) — the "Sources" pool (individual controllable sources). Prefer cfg.sources; if it's
// empty (a CFG saved under the earlier "Brands" design), fall back to the old brand pool so nothing
// already typed is lost. De-duped, order-preserving.
export const sourcePool = (cfg: RnwyCfg | null | undefined): string[] => {
  if (!cfg) return [];
  if (cfg.sources && cfg.sources.length) return cfg.sources;
  return brandPool(cfg);
};

// item 647 — the CFG editor shows a base rate AND its marked-up "actual" value beside it. These
// mirror the engine's markup math (base × multiplier) so the editor previews what the engine prices:
//   config/catalog marked-up = base × ConfigIncrease ; perpetual % marked-up = pct × PerpetualIncrease
//   help-desk actual = base × HelpDeskFirst/AddOnIncrease (by scope)
export const markupMoney = (base: number, mult: number): number => (base || 0) * (mult || 0);
export const markupPct = (pct: number, mult: number): number => (pct || 0) * (mult || 0);
